import { useContext, useEffect, useRef, useState } from 'react';

import { Auth } from 'aws-amplify';
import { useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import {
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import config from 'config';
import { AuthContext, AuthContextAction, AuthState, AuthUser } from 'context/AuthContext';
import { FeatureFlagProvider } from 'context/FeatureFlagContext';
import { LinkButton, Spinner } from 'ui-kit';

import { GET_FEATURE_FLAGS } from '../../query/getFeatureFlags';
import { SSO_CALLBACK } from '../../mutation/sso';
import { retrieveAndClearCodeVerifier, base64UrlDecode } from '../../utils/pkce';
import { initiateSsoRedirect } from '../../utils/ssoLogin';
import {
  clearSsoRetrySession,
  decideSsoRetryAction,
  readSsoRetrySession,
  recordRetryAttempt,
} from '../../utils/ssoRetry';
import { normalizeFeatureFlags } from '../../utils/normalizeFeatureFlags';
import FooterMenu from './FooterMenu';
import ForgotPassword from './ForgotPassword';
import HeaderMenu from './HeaderMenu';
import LoginMarketingMessage from './LoginMarketingMessage';
import NewPasswordRequired from './NewPasswordRequired';
import { SignInForm } from './SignInForm';

type Credentials = {
  email: string;
  password: string;
};

const UserAuthentication = (): JSX.Element => {
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [credentials, setCredentials] = useState<Credentials>({
    email: '',
    password: '',
  });
  const [userForPasswordChange, setUserForPasswordChange] = useState<AuthUser | null>(null);
  const { state, dispatch } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [ssoCallback] = useMutation(SSO_CALLBACK);

  // Fetch feature flags even when not authenticated
  // This query may fail if it requires auth, but we handle that gracefully
  const { data: featureFlagsData, loading: featureFlagsLoading } = useQuery(GET_FEATURE_FLAGS, {
    errorPolicy: 'ignore', // Don't throw errors if query fails
    fetchPolicy: 'cache-first',
  });

  const featureFlags = featureFlagsData ? normalizeFeatureFlags(featureFlagsData.featureFlags) : {};
  const featureFlagsLoaded = !featureFlagsLoading || !!featureFlagsData;

  // Kill switch for the transient-timeout SSO auto-retry (ENG-2910). Read from
  // the query-derived locals rather than the useFeatureFlag hook because this
  // component renders FeatureFlagProvider (it sits above the provider).
  const ssoAutoRetryEnabled =
    featureFlagsLoaded &&
    !!featureFlags['ssoAutoRetry'] &&
    featureFlags['ssoAutoRetry'] !== 'disabled';

  // Used only by the retry effect below. That effect re-runs when the feature
  // flags resolve (the deferral), and React StrictMode double-invokes it in dev,
  // so this ref keeps one logical retry from incrementing the attempt counter
  // more than once.
  const retryStartedRef = useRef(false);

  // Handle SSO callback
  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  useEffect(() => {
    if (!error) return;

    // Surface an SSO error to the user and clear the retry session + URL.
    const surfaceError = (message: string) => {
      clearSsoRetrySession();
      setIsLoading(false);
      dispatch({ type: AuthContextAction.AUTH_ERROR, payload: { error: message } });
      setSearchParams({}, { replace: true });
    };

    // Decide what to do (pure); this effect just executes the decision. See
    // decideSsoRetryAction for the branching rationale (ENG-2910).
    const decision = decideSsoRetryAction({
      errorDescription,
      session: readSsoRetrySession(),
      featureFlagsLoaded,
      ssoAutoRetryEnabled,
    });

    if (decision.kind === 'defer') {
      // Feature flags haven't resolved yet (fresh page load). Hold the spinner;
      // the effect re-runs when featureFlagsLoaded flips.
      setIsLoading(true);
      return;
    }

    if (decision.kind === 'retry') {
      if (retryStartedRef.current) return; // StrictMode double-invoke guard
      retryStartedRef.current = true;
      setIsLoading(true);

      // recordRetryAttempt bumps the (bounded) count and refreshes the session
      // TTL. If it can't persist, we can't bound the loop — fail closed.
      if (!recordRetryAttempt()) {
        retryStartedRef.current = false;
        surfaceError('Single sign-on failed. Please try again, or contact support@orsnn.com.');
        return;
      }

      // Navigates away; no need to clear the URL. If the redirect itself can't
      // start (e.g. PKCE unsupported), give up cleanly.
      initiateSsoRedirect(decision.login, config.website.URL).catch((retryErr) => {
        retryStartedRef.current = false;
        console.error('SSO auto-retry could not start', retryErr);
        surfaceError(
          'Single sign-on failed to start. Please try again, or contact support@orsnn.com.',
        );
      });
      return;
    }

    // decision.kind === 'giveUp'
    console.error('SSO callback error', error, errorDescription);
    surfaceError(
      decision.exhausted
        ? "We couldn't reach your single sign-on provider after a few tries. Please try again in a moment."
        : 'Single sign-on failed. Please try again, or contact support@orsnn.com.',
    );
  }, [error, errorDescription, ssoAutoRetryEnabled, featureFlagsLoaded, dispatch, setSearchParams]);

  // Success path: the single-use SSO code exchange. Deliberately does NOT depend
  // on the feature flags (only the retry effect above does), so it runs once for
  // the callback and never re-enters mid-flight — no one-shot ref needed.
  useEffect(() => {
    if (!code) return;

    // We're past the Cognito<->IdP exchange, so the transient-timeout window is
    // over — drop the retry session.
    clearSsoRetrySession();

    // Process SSO callback
    const codeVerifier = retrieveAndClearCodeVerifier();
    if (!codeVerifier) {
      dispatch({
        type: AuthContextAction.AUTH_ERROR,
        payload: { error: 'SSO session expired. Please try again.' },
      });
      setSearchParams({}, { replace: true });
      return;
    }

    setIsLoading(true);

    const redirectUri = config.website.URL;
    ssoCallback({
      variables: {
        code,
        codeVerifier,
        redirectUri,
      },
    })
      .then(async ({ data }) => {
        const result = data?.ssoCallback;

        if (result?.success && result?.user && result?.idToken && result?.accessToken) {
          try {
            // Create Cognito token objects from the token strings
            const idToken = new CognitoIdToken({ IdToken: result.idToken });
            const accessToken = new CognitoAccessToken({
              AccessToken: result.accessToken,
            });
            const refreshToken = result.refreshToken
              ? new CognitoRefreshToken({ RefreshToken: result.refreshToken })
              : undefined;

            // Create a CognitoUserSession from the tokens
            const session = new CognitoUserSession({
              IdToken: idToken,
              AccessToken: accessToken,
              RefreshToken: refreshToken,
            });

            // Decode the ID token to get the username
            const tokenPayload = JSON.parse(base64UrlDecode(result.idToken.split('.')[1]));
            const username = tokenPayload['cognito:username'] || tokenPayload.sub;

            // Get Amplify's userPool to ensure we use the same storage instance
            // This is critical - using Amplify's userPool ensures getCurrentUser() works
            const userPool = new CognitoUserPool({
              UserPoolId: config.cognito.USER_POOL_ID,
              ClientId: config.cognito.APP_CLIENT_ID,
            });

            // Create a CognitoUser with the username and user pool
            const cognitoUser = new CognitoUser({
              Username: username,
              Pool: userPool,
            });

            // Set the session on the user
            // This stores the session in localStorage using the pool's storage
            cognitoUser.setSignInUserSession(session);

            // Sync with Amplify by calling currentAuthenticatedUser
            // This ensures Amplify's internal state recognizes the session
            // and returns a user from the same pool/storage that getCurrentUser() uses
            const amplifyUser = await Auth.currentAuthenticatedUser();

            // Dispatch the user from Amplify to ensure consistency
            dispatch({
              type: AuthContextAction.SET_SIGNED_IN_USER,
              payload: amplifyUser as AuthUser,
            });

            // Clean up URL and stop loading
            setSearchParams({}, { replace: true });
            setIsLoading(false);
          } catch (error: any) {
            // SECURITY: Only log error message, not the full error object
            // to avoid accidentally logging tokens if they're in the error
            console.error(
              'Failed to create Amplify session from SSO tokens:',
              error.message || 'Unknown error',
            );
            setIsLoading(false);
            dispatch({
              type: AuthContextAction.AUTH_ERROR,
              payload: {
                error: `Failed to initialize session: ${error.message || 'Unknown error'}. Please try again.`,
              },
            });
            setSearchParams({}, { replace: true });
          }
        } else {
          setIsLoading(false);
          dispatch({
            type: AuthContextAction.AUTH_ERROR,
            payload: {
              error: result?.error || 'SSO authentication failed',
            },
          });
          setSearchParams({}, { replace: true });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        dispatch({
          type: AuthContextAction.AUTH_ERROR,
          payload: {
            error: err.message || 'SSO authentication failed',
          },
        });
        setSearchParams({}, { replace: true });
      });
  }, [code, dispatch, setSearchParams, ssoCallback]);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) =>
        dispatch({
          type: AuthContextAction.SET_SIGNED_IN_USER,
          payload: user,
        }),
      )
      .catch(() =>
        dispatch({
          type: AuthContextAction.SIGN_OUT,
        }),
      );
  }, [dispatch]);

  const handleNewPasswordRequired = (user: AuthUser, creds: Credentials) => {
    setUserForPasswordChange(user);
    setCredentials(creds);
    setIsNewPasswordRequired(true);
    setIsForgotPassword(false);
  };

  const handleNewPasswordComplete = () => {
    setIsNewPasswordRequired(false);
    setUserForPasswordChange(null);
    setCredentials({ email: '', password: '' });
    setIsForgotPassword(false);
  };

  const handleBackToSignIn = () => {
    setIsForgotPassword(false);
    setIsNewPasswordRequired(false);
    setUserForPasswordChange(null);
    setCredentials({ email: '', password: '' });
  };

  return (
    <FeatureFlagProvider flags={featureFlags} loaded={featureFlagsLoaded}>
      <div
        id="UserAuthentication"
        className="relative h-screen min-h-screen w-full animate-auth-bg  overflow-y-auto bg-auth-gradient bg-[length:200%_200%] text-white"
      >
        <HeaderMenu />

        <div id="UserAuthentication_content" className="mb-[150px] flex flex-1 flex-col px-4">
          <div className="mt-6 flex w-full max-w-[1440px] flex-col items-center justify-center gap-12 self-center md:mt-[150px] md:flex-row md:items-start md:justify-between">
            <div className="w-full max-w-[800px]">
              <LoginMarketingMessage />
            </div>

            {state.authState === AuthState.SignedOut && (
              <div className="w-full max-w-[450px] md:w-[450px]">
                <div className="relative transition-all duration-500 ease-in-out">
                  {isNewPasswordRequired ? (
                    <div className="rounded-md border border-violet-600 bg-slate-950 p-10 text-white shadow-md">
                      {isLoading && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-md bg-black bg-opacity-50">
                          <Spinner size={80} loading={true} />
                        </div>
                      )}
                      <NewPasswordRequired
                        user={userForPasswordChange!}
                        onComplete={handleNewPasswordComplete}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                      />
                    </div>
                  ) : !isForgotPassword ? (
                    <div
                      className={`transition-opacity duration-500 ${
                        isForgotPassword || isNewPasswordRequired
                          ? 'hidden opacity-0'
                          : 'block opacity-100'
                      }`}
                    >
                      <div className="relative rounded-md border border-violet-600 bg-slate-950 p-10 text-white shadow-md">
                        {isLoading && (
                          <div className="absolute inset-0 z-50 flex items-center justify-center rounded-md bg-black bg-opacity-50">
                            <Spinner size={80} loading={true} />
                          </div>
                        )}
                        <SignInForm
                          setIsNewPasswordRequired={(user: AuthUser, creds: Credentials) =>
                            handleNewPasswordRequired(user, creds)
                          }
                          setCredentials={setCredentials}
                          onFlip={() => setIsForgotPassword(true)}
                          setIsLoading={setIsLoading}
                          isLoading={isLoading}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <p className="text-white">Don't have an account?</p>
                        <LinkButton
                          text="Contact sales"
                          url="https://orsnn.com/contact-us/"
                          underline={false}
                          contentClassName="font-heebo text-pink-400"
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`transition-opacity duration-500 ${
                        isForgotPassword && !isNewPasswordRequired
                          ? 'block opacity-100'
                          : 'hidden opacity-0'
                      }`}
                    >
                      <div className="rounded-md border border-violet-600 bg-slate-950 p-10 text-white shadow-md">
                        <ForgotPassword onFlip={handleBackToSignIn} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <FooterMenu />
      </div>
    </FeatureFlagProvider>
  );
};

export default UserAuthentication;
